import * as React from "react";
import { Chip, ChipProps, Box, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ObjectValidationStatus } from "../../project/validationProcess/validationProcessApi";


// mapka labeli PL/EN
const LABELS: Record<"pl" | "en", Record<ObjectValidationStatus, string>> = {
    pl: {
        NOT_VALIDATED: "NIEZWALIDOWANY",
        NOT_APPLICABLE: "Nie dotyczy",
        IN_PROGRESS: "W toku",
        VALIDATED: "Zwalidowany",
        REVALIDATION_REQUIRED: "Wymagana rewalidacja",
    },
    en: {
        NOT_VALIDATED: "NOT VALIDATED",
        NOT_APPLICABLE: "Not applicable",
        IN_PROGRESS: "In progress",
        VALIDATED: "Validated",
        REVALIDATION_REQUIRED: "Revalidation required",
    },
};

export const getValidationStatusLabel = (lang: 'pl' | 'en', status: ObjectValidationStatus): string => {
    return LABELS[lang][status]
}

// kolory (MUI palette keys)
const PALETTE_KEY: Record<ObjectValidationStatus, "default" | "success" | "warning" | "error" | "info"> = {
    NOT_VALIDATED: "error",
    NOT_APPLICABLE: "default",
    IN_PROGRESS: "warning",
    VALIDATED: "success",
    REVALIDATION_REQUIRED: "error",
};

// (opcjonalnie) krótkie opisy do tooltipa
const HINTS: Record<"pl" | "en", Record<ObjectValidationStatus, string>> = {
    pl: {
        NOT_VALIDATED: "System powinien być zwalidowany, ale nie jest.",
        NOT_APPLICABLE: "Poza zakresem GxP / walidacja nie dotyczy.",
        IN_PROGRESS: "Walidacja w toku (dokumenty/testy).",
        VALIDATED: "Walidacja zakończona pozytywnie.",
        REVALIDATION_REQUIRED: "Wymagane działania rewalidacyjne/przegląd.",
    },
    en: {
        NOT_VALIDATED: "System should be validated but is not.",
        NOT_APPLICABLE: "Out of GxP scope / validation not applicable.",
        IN_PROGRESS: "Validation in progress (docs/tests).",
        VALIDATED: "Validation completed successfully.",
        REVALIDATION_REQUIRED: "Revalidation / periodic review required.",
    },
};

// helper: normalizacja wartości (przyjmie string, number, null)
export const toValidationStatus = (v: unknown): ObjectValidationStatus | undefined => {
    if (v == null) return undefined;

    // mapowanie liczb (gdy backend zwraca inty)
    const num = Number(v as any);
    if (!Number.isNaN(num)) {
        const byNum: Record<number, ObjectValidationStatus> = {
            0: "NOT_APPLICABLE",
            1: "IN_PROGRESS",
            2: "VALIDATED",
            3: "REVALIDATION_REQUIRED",
            4: "NOT_VALIDATED", // jeśli używasz 4
        };
        if (byNum[num]) return byNum[num];
    }

    // mapowanie stringów (również camelCase -> UPPER_SNAKE)
    const s = String(v).trim();
    const map: Record<string, ObjectValidationStatus> = {
        NOT_VALIDATED: "NOT_VALIDATED",
        NOT_APPLICABLE: "NOT_APPLICABLE",
        IN_PROGRESS: "IN_PROGRESS",
        VALIDATED: "VALIDATED",
        REVALIDATION_REQUIRED: "REVALIDATION_REQUIRED",
        NotValidated: "NOT_VALIDATED",
        NotApplicable: "NOT_APPLICABLE",
        InProgress: "IN_PROGRESS",
        Validated: "VALIDATED",
        RevalidationRequired: "REVALIDATION_REQUIRED",
    };
    return map[s] ?? map[s.toUpperCase()];
};

type Props = {
    status: ObjectValidationStatus | string | number | null | undefined;
    lang?: "pl" | "en";
    size?: ChipProps["size"]; // "small" | "medium"
    withTooltip?: boolean;
};

export const ObjectValidationStatusChip: React.FC<Props> = ({
    status,
    lang = "pl",
    size = "small",
    withTooltip = true,
}) => {
    const st = toValidationStatus(status);
    if (!st) return null;

    const paletteKey = PALETTE_KEY[st];
    const label = LABELS[lang][st];
    const hint = HINTS[lang][st];

    const chip = (
        <Chip
            size={size}
            variant="outlined"
            color={paletteKey}
            icon={
                <Box
                    sx={(t) => ({
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: paletteKey === "default" ? t.palette.text.disabled : t.palette[paletteKey].main,
                    })}
                />
            }
            label={label}
            sx={(t) => ({
                bgcolor:
                    paletteKey === "default"
                        ? t.palette.action.hover
                        : alpha(t.palette[paletteKey].main, t.palette.mode === "dark" ? 0.16 : 0.1),
                borderColor: paletteKey === "default" ? t.palette.divider : alpha(t.palette[paletteKey].main, 0.5),
                color: t.palette.text.primary,
                fontWeight: 800,
                letterSpacing: ".02em",
                borderRadius: 1,
                height: 24,
                "& .MuiChip-label": { px: 1, py: 0.25, fontSize: "0.75rem", lineHeight: 1.6 },
                "& .MuiChip-icon": { mr: 0.5 },
            })}
        />
    );

    return withTooltip ? <Tooltip title={hint}><span>{chip}</span></Tooltip> : chip;
};
